import { useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { Reveal } from "../components/site/Reveal";

const SECTIONS = [
  { id: "about", title: "About Us" },
  { id: "shipping", title: "Shipping" },
  { id: "returns", title: "Returns" },
  { id: "faq", title: "FAQ" },
  { id: "contact", title: "Contact" },
];

function Block({ id, title, children }) {
  return (
    <section id={id} data-testid={`help-section-${id}`} className="scroll-mt-40 border-t border-plum/10 py-12 first:border-t-0">
      <h2 className="font-display text-3xl sm:text-4xl italic">{title}</h2>
      <div className="mt-5 space-y-4 leading-relaxed text-plum/85">{children}</div>
    </section>
  );
}

const FAQS = [
  ["How long does shipping take?", "We process orders within 1–3 business days. Estimated delivery is 3–7 business days after your order ships."],
  ["When do I get free shipping?", "Orders of $75 or more ship free. For other orders, shipping charges are displayed at checkout before you complete your purchase."],
  ["Can I track my order?", "Yes — as soon as your order ships we email you a tracking number and a Track Your Package link."],
  ["Are the colors exact?", "We photograph every piece in natural light, but screens vary. If a color disappoints, our returns policy has you covered."],
  ["How do I use a sale code?", "Sale prices apply automatically — no code needed at checkout."],
];

export default function Help() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!API) return toast.error("Contact form is unavailable right now");
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received — we'll be in touch!");
    } catch {
      toast.error("Please fill in your name, a valid email and a short message");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main data-testid="help-page" className="mx-auto max-w-4xl px-5 sm:px-8 pt-12 pb-28">
      <p className="eyebrow text-gold-deep">Here to help</p>
      <h1 className="mt-2 font-script text-6xl sm:text-7xl">Good to Know</h1>
      <nav className="mt-8 flex flex-wrap gap-2" data-testid="help-nav">
        {SECTIONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} data-testid={`help-nav-${s.id}`} className="rounded-full border border-plum/20 px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-plum transition-colors hover:bg-plum hover:text-cream">
            {s.title}
          </a>
        ))}
      </nav>

      <div className="mt-10">
        <Block id="about" title="About Us">
          <p className="font-display text-2xl italic text-plum">Welcome to Luxe Dream Market — where style meets lifestyle!</p>
          <p>Luxe Dream Market is your destination for discovering fashionable finds, everyday essentials, standout accessories, and lifestyle products that bring a little extra joy to everyday living.</p>
          <p>We believe shopping should be fun, inspiring, and accessible. That's why we're creating a shopping experience filled with vibrant style, unique finds, and products that help you express yourself with confidence.</p>
          <p>Whether you're refreshing your wardrobe, searching for the perfect accessory, or discovering something new for your everyday life, we're here to make your shopping experience exciting and enjoyable.</p>
          <p>Our mission is simple: to bring you a variety of products you'll love, a welcoming shopping experience, and inspiration to live your style your way.</p>
          <p>Thank you for being part of the Luxe Dream Market family. Your support means the world to us!</p>
          <p className="font-script text-4xl text-magenta">Discover Your Style. Love Your Lifestyle.</p>
        </Block>

        <Block id="shipping" title="Shipping">
          <p className="font-display text-lg italic text-plum">Thank you for shopping with Luxe Dream Market!</p>
          <ul className="space-y-2.5">
            <li><strong>Order Processing:</strong> Please allow 1–3 business days for order processing before your order ships.</li>
            <li><strong>Shipping Times:</strong> Estimated delivery is 3–7 business days after your order ships. Delivery times may vary depending on your location, supplier, and carrier.</li>
            <li><strong>Shipping Costs:</strong> Shipping charges will be displayed at checkout before you complete your purchase. Orders of $75 or more always ship free.</li>
            <li><strong>Order Tracking:</strong> Once your order ships, you'll receive a shipping confirmation with tracking information when available.</li>
            <li><strong>Shipping Delays:</strong> Luxe Dream Market is not responsible for delays caused by shipping carriers, severe weather, or other circumstances beyond our control.</li>
          </ul>
          <p>Questions? Please use the contact form below for assistance with your order.</p>
        </Block>

        <Block id="returns" title="Return Policy">
          <p className="font-display text-lg italic text-plum">Your shopping experience matters to us! We want you to feel confident and happy with every purchase.</p>
          <ul className="space-y-2.5">
            <li><strong>Return Requests:</strong> Eligible items may be returned within 14 days of delivery. Items must be unused, unworn, unwashed, and in their original condition, with tags and packaging where applicable.</li>
            <li><strong>Damaged or Incorrect Items:</strong> If your order arrives damaged, defective, or incorrect, please contact us with your order number and photos so we can help make things right.</li>
            <li><strong>Refunds:</strong> Once an approved return is received and inspected, we'll notify you of the outcome. Approved refunds will be issued to your original payment method, with processing times depending on your payment provider.</li>
            <li><strong>Return Shipping:</strong> Customers are responsible for return shipping costs unless the item is defective, damaged, incorrect, or applicable law requires otherwise.</li>
            <li><strong>How to Start a Return:</strong> Please contact us through our website before sending anything back. We'll provide instructions for your return.</li>
            <li>Some items may be excluded from returns when clearly identified at purchase and permitted by law.</li>
          </ul>
          <p>Thank you for shopping with Luxe Dream Market. We appreciate your support and look forward to serving you again!</p>
        </Block>

        <Block id="faq" title="FAQ">
          <div className="space-y-5">
            {FAQS.map(([q, a]) => (
              <div key={q}>
                <p className="font-display text-lg italic text-plum">{q}</p>
                <p className="mt-1">{a}</p>
              </div>
            ))}
          </div>
        </Block>

        <Block id="contact" title="Contact">
          {sent ? (
            <p data-testid="contact-success" className="font-display text-2xl italic text-magenta">Message received — we'll be in touch soon, darling.</p>
          ) : (
            <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="contact-form">
              <input required maxLength={80} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" className="rounded-full border border-plum/20 bg-white px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-magenta" />
              <input required type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" className="rounded-full border border-plum/20 bg-white px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-magenta" />
              <textarea required minLength={5} maxLength={2000} rows={4} placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" className="sm:col-span-2 rounded-3xl border border-plum/20 bg-white px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-magenta" />
              <button type="submit" disabled={busy} data-testid="contact-submit-button" className="btn-luxe sm:col-span-2 w-fit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send message</button>
            </form>
          )}
        </Block>
      </div>
    </main>
  );
}
